import { useState, useEffect, useMemo } from 'react';
import { attendanceService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import PoderUploadBox from './PoderUploadBox';
import styles from './DarPoderModal.module.css';
import { nombreDe, etiquetaUnidad, coefDe, poderesPorUnidad, agruparPorPropietario, coefYaRepresentado } from './poderes';

const DOC_EXTERNO_RE = /^[A-Za-z0-9]{5,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "¿A quién le da poder QAA?": se abre desde la fila del propietario que
// ENTREGA el poder. Elige al representante — un vecino del conjunto (lista
// con buscador) o una persona externa — y qué unidades entrega (si tiene
// varias, todas las libres marcadas por defecto). Mismo endpoint que
// "Representa a…": POST /attendances/representative/:id.
export default function DarPoderModal({ giverItem, items, assemblyId, onClose, onSaved }) {
  const [tab,      setTab]      = useState('propietario');
  const [info,     setInfo]     = useState(null);
  const [infoErr,  setInfoErr]  = useState('');
  const [search,   setSearch]   = useState('');
  const [repId,    setRepId]    = useState(null);   // owner.id del vecino elegido
  const [ext,      setExt]      = useState({ nombres: '', apellidos: '', doc: '', email: '' });
  const [file,     setFile]     = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  useEffect(() => {
    attendanceService.powerInfo(assemblyId)
      .then(r => setInfo(r.data))
      .catch(e => setInfoErr(e.response?.data?.message || 'No se pudo cargar la información de poderes.'));
  }, [assemblyId]);

  const giver     = giverItem.owner;
  const giverName = nombreDe(giver);
  const porUnidad = useMemo(() => poderesPorUnidad(items), [items]);
  const giverRows = useMemo(() => items.filter(i => i.owner?.id === giver?.id).sort((a, b) => a.id - b.id), [items, giver]);
  const libres    = giverRows.filter(r => !porUnidad.has(Number(r.unit_id)));
  const [units, setUnits] = useState(() => libres.map(r => r.unit_id));

  const vecinos = useMemo(() => agruparPorPropietario(items).filter(g => g.owner.id !== giver?.id), [items, giver]);
  const q = search.trim().toLowerCase();
  const visibles = q
    ? vecinos.filter(g => nombreDe(g.owner).toLowerCase().includes(q)
        || String(g.owner.numero_identificacion).includes(q)
        || g.rows.some(r => etiquetaUnidad(r).toLowerCase().includes(q)))
    : vecinos;
  const rep = vecinos.find(g => g.owner.id === repId) || null;

  const filasEntrega = giverRows.filter(r => units.includes(r.unit_id));
  const numerosEntrega = filasEntrega.map(etiquetaUnidad).join(', ');
  const coefTotal = info?.coef_total || 0;
  const maxPct    = info?.max_pct || 20;
  const coefRep   = tab === 'propietario' && rep ? coefYaRepresentado(items, rep.owner.numero_identificacion) : 0;
  const pct       = coefTotal > 0 ? ((coefRep + coefDe(filasEntrega)) / coefTotal) * 100 : 0;
  const excede    = coefTotal > 0 && pct > maxPct + 1e-9;
  const finalizada = info?.assembly_status === 'finalizada';

  const docErr   = ext.doc && !DOC_EXTERNO_RE.test(ext.doc) ? 'Entre 5 y 20 letras o números, sin puntos ni espacios.' : '';
  const emailErr = ext.email && !EMAIL_RE.test(ext.email.trim()) ? 'Correo no válido.' : '';
  const repNombre = tab === 'propietario' ? (rep ? nombreDe(rep.owner) : '') : `${ext.nombres.trim()} ${ext.apellidos.trim()}`.trim();

  const listo = !!info && !finalizada && units.length > 0 && !!file && !excede && (tab === 'propietario'
    ? !!rep && !!info.accounts?.[rep.owner.id]
    : ext.nombres.trim() && ext.apellidos.trim() && DOC_EXTERNO_RE.test(ext.doc) && EMAIL_RE.test(ext.email.trim()));

  const guardar = async () => {
    setError('');
    if (!listo) return setError('Completa los datos del representante, las unidades y el poder firmado.');
    setSaving(true);
    try {
      const fd = new FormData();
      if (tab === 'propietario') {
        fd.append('rep_nombre',           nombreDe(rep.owner));
        fd.append('rep_numero_documento', rep.owner.numero_identificacion);
        fd.append('rep_es_externo',       'false');
      } else {
        fd.append('rep_nombres',          ext.nombres.trim());
        fd.append('rep_apellidos',        ext.apellidos.trim());
        fd.append('rep_numero_documento', ext.doc);
        fd.append('rep_es_externo',       'true');
        fd.append('rep_email',            ext.email.trim());
      }
      fd.append('rep_unidades', JSON.stringify(units));
      fd.append('documento',    file);
      const r = await attendanceService.registerRepresentative(filasEntrega[0].id, fd);
      onSaved(`${giverName} dio poder a ${repNombre}.`, false, r.data?.aviso || null);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al registrar el poder');
    } finally { setSaving(false); }
  };

  const tabBtn = (id, label, icon) => (
    <button type="button" onClick={() => { setTab(id); setError(''); }}
      className={tab === id ? `${styles.pestana} ${styles.pestanaActiva}` : `${styles.pestana} ${styles.pestanaInactiva}`}>
      <i className={`fas ${icon}`} />{label}
    </button>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.caja}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>
              <i className={`fas fa-file-signature ${styles.iconoTitulo}`} />
              ¿A quién le da poder {giverName}?
            </h3>
            <div className={styles.subtitulo}>
              {giverName} · CC {giver?.numero_identificacion} — entrega su voto para esta asamblea
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className={`modal-body ${styles.cuerpo}`}>
          {/* Arriba y fijo (AvisoModal): el error que dejaba al FINAL del cuerpo quedaba fuera de la vista, porque «Registrar» está en el pie fijo. */}
          {error && <AvisoModal>{error}</AvisoModal>}
          {infoErr && <div className="alert alert-danger">{infoErr}</div>}
          {!info && !infoErr && <div className="loading"><div className="spinner" /></div>}
          {info && finalizada && <div className="alert alert-danger">La asamblea ya finalizó: no se pueden registrar poderes.</div>}
          {info && !finalizada && libres.length === 0 && (
            <div className={`alert alert-warning ${styles.avisoSinMargen}`}>
              Todas las unidades de {giverName} ya tienen poder. Para cambiarlo, usa <strong>Ver poder → Quitar poder</strong>.
            </div>
          )}

          {info && !finalizada && libres.length > 0 && (
            <>
              <div className={styles.pestanas}>
                {tabBtn('propietario', 'Un propietario del conjunto', 'fa-building')}
                {tabBtn('externo', 'Persona externa', 'fa-user-plus')}
              </div>

              {tab === 'propietario' ? (
                <div>
                  <input className="form-control" value={search} aria-label="Buscar representante"
                    onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, documento o unidad" />
                  <div className={styles.vecinos}>
                    {visibles.map(g => {
                      const activa = !!info.accounts?.[g.owner.id];
                      const elegido = repId === g.owner.id;
                      return (
                        <label key={g.owner.id} className={`${styles.vecino}${elegido ? ` ${styles.vecinoElegido}` : ''}${activa ? '' : ` ${styles.vecinoInactivo}`}`}>
                          <input type="radio" name="dar-poder-rep" checked={elegido} disabled={!activa}
                            onChange={() => setRepId(g.owner.id)} className={styles.radio} />
                          <div>
                            <div className={styles.vecinoNombre}>{nombreDe(g.owner)}</div>
                            <div className={styles.vecinoDatos}>
                              CC {g.owner.numero_identificacion} · Unidad{g.rows.length > 1 ? 'es' : ''} {g.rows.map(etiquetaUnidad).join(', ')}
                            </div>
                            {!activa && <div className={styles.vecinoAviso}>Aún no activa su cuenta: no podría votar.</div>}
                          </div>
                        </label>
                      );
                    })}
                    {visibles.length === 0 && <div className={styles.sinResultados}>No hay propietarios que coincidan.</div>}
                  </div>
                </div>
              ) : (
                <div className={styles.externo}>
                  <div className={styles.externoInfo}>
                    Alguien que <strong>no</strong> es propietario del conjunto. Recibe un acceso temporal que se elimina al finalizar la asamblea.
                  </div>
                  <div className={styles.dosColumnas}>
                    <div className={`form-group ${styles.sinMargen}`}>
                      <label className="form-label" htmlFor="dp-nombres">Nombres *</label>
                      <input id="dp-nombres" className="form-control" value={ext.nombres} maxLength={60}
                        onChange={e => setExt(x => ({ ...x, nombres: e.target.value }))} placeholder="Ej. María José" />
                    </div>
                    <div className={`form-group ${styles.sinMargen}`}>
                      <label className="form-label" htmlFor="dp-apellidos">Apellidos *</label>
                      <input id="dp-apellidos" className="form-control" value={ext.apellidos} maxLength={60}
                        onChange={e => setExt(x => ({ ...x, apellidos: e.target.value }))} placeholder="Ej. Pérez Gómez" />
                    </div>
                  </div>
                  <div className={styles.dosColumnas}>
                    <div className={`form-group ${styles.sinMargen}`}>
                      <label className="form-label" htmlFor="dp-doc">Documento *</label>
                      <input id="dp-doc" className="form-control" value={ext.doc} maxLength={20}
                        onChange={e => setExt(x => ({ ...x, doc: e.target.value.replace(/[^A-Za-z0-9]/g, '') }))} placeholder="Cédula o pasaporte" />
                      {docErr && <div className={styles.errorCampo}>{docErr}</div>}
                    </div>
                    <div className={`form-group ${styles.sinMargen}`}>
                      <label className="form-label" htmlFor="dp-email">Correo electrónico *</label>
                      <input id="dp-email" type="email" className="form-control" value={ext.email}
                        onChange={e => setExt(x => ({ ...x, email: e.target.value }))} placeholder="correo@ejemplo.com" />
                      {emailErr && <div className={styles.errorCampo}>{emailErr}</div>}
                    </div>
                  </div>
                </div>
              )}

              {libres.length > 1 && (
                <div>
                  <div className={styles.unidadesTitulo}>Unidades que entrega</div>
                  <div className={styles.unidades}>
                    {libres.map(row => {
                      const on = units.includes(row.unit_id);
                      return (
                        <button key={row.unit_id} type="button"
                          onClick={() => setUnits(u => on ? u.filter(x => x !== row.unit_id) : [...u, row.unit_id])}
                          className={`${styles.chip} ${on ? styles.chipOn : styles.chipOff}`}>
                          <i className={`fas ${on ? 'fa-check-square' : 'fa-square'} ${styles.chipIcono}`} />
                          {etiquetaUnidad(row)}
                          {coefTotal > 0 && ` · ${(parseFloat(row.unit?.coefficiente_votacion) || 0).toFixed(4)}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <PoderUploadBox id="dar-poder-doc" ownerName={giverName} file={file} onFile={setFile} onError={setError} />

              {coefTotal > 0 && (tab === 'externo' || rep) && (
                <div>
                  <div className={styles.barraEncabezado}>
                    <span>Coeficiente que representaría {repNombre || 'el representante'}</span>
                    <strong className={excede ? `${styles.pct} ${styles.pctExcede}` : styles.pct}>{pct.toFixed(1)}% de {maxPct}% permitido</strong>
                  </div>
                  <div className={styles.barraPista}>
                    <div className={excede ? `${styles.barraRelleno} ${styles.barraExcede}` : styles.barraRelleno}
                      style={{ '--ancho': `${Math.min(100, (pct / maxPct) * 100)}%` }} />
                  </div>
                  {excede && <div className={styles.excedeTexto}>Supera el tope de la Ley 675 para ese representante.</div>}
                </div>
              )}

              {repNombre && units.length > 0 && !excede && (
                <div className={styles.resumen}>
                  <i className={`fas fa-file-signature ${styles.resumenIcono}`} />
                  <strong>{repNombre}</strong> votará en nombre de <strong>{giverName}</strong> (unidad{units.length > 1 ? 'es' : ''} {numerosEntrega}).
                </div>
              )}
            </>
          )}

        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className={`btn btn-primary ${styles.botonRegistrar}${!listo && !saving ? ` ${styles.botonInactivo}` : ''}`} onClick={guardar} disabled={saving || !listo}>
            {saving ? <><i className="fas fa-spinner fa-spin" /> Guardando...</> : <><i className="fas fa-file-signature" /> Registrar poder</>}
          </button>
        </div>
      </div>
    </div>
  );
}
